import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import type { BasePagination } from "../../../types/api.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"

/** Invoice origins mirrored from the backend IntegerChoices. */
export const INVOICE_ORIGINS = {
  GENERATED: 1,
  EXTERNAL: 2
} as const

/** Invoice statuses mirrored from the backend IntegerChoices. */
export const INVOICE_STATUSES = {
  PENDING: 1,
  PAID: 2,
  OVERDUE: 3,
  DRAFT: 4,
  ISSUED: 5,
  PARTIALLY_PAID: 6,
  VOID: 7
} as const

export interface InvoiceProjectRef {
  id: string
  uid: string | null
  lead_name: string
}

export interface InvoiceLineBilling {
  id: string
  finance_line: string
  finance_line_name?: string
  amount: string
}

/** Cross-project list item: ProjectInvoice plus the additive billing fields. */
export interface InvoiceListItem extends ProjectInvoice {
  project_info: InvoiceProjectRef | null
  // Multi-branch: which branch this invoice belongs to — `null` for a
  // legacy row whose Team predates the branch feature.
  branch_name: string | null
  origin: number
  origin_display: string
  subtotal: string | null
  tax_pct: string | null
  tax_amount: string | null
  retainage_pct: string | null
  retainage_amount: string | null
  paid_to_date: string
  notes?: string
  /** How large the PDF's top block is printed, in percent. */
  header_scale?: InvoiceHeaderScale
  customer_email?: string | null
  lines?: InvoiceLineBilling[]
}

export interface BillableLine {
  id: string
  name: string
  planned_value: string
  previously_billed: string
  // Money already collected against the line outside the invoicing flow —
  // together with previously_billed, decides why balance_remaining hit zero
  // (see IssueInvoiceDrawer's fullyBilled/fullyPaidUnbilled split).
  previously_paid?: string
  balance_remaining: string
  entry_type: string
  responsible_type: number | null
  classification?: string | null
  reimbursable?: boolean
  /** The milestone the line belongs to (newer backends). */
  project_phase?: string | null
  project_phase_name?: string | null
  item_type?: number | null
  /** The line's own status (newer backends); a Cancelled line takes no billing. */
  status?: number | null
  custom_subcontractor_name?: string | null
  /** Who a payable line pays. */
  subcontractor?: string | null
  subcontractor_name?: string | null
  supplier?: string | null
  supplier_name?: string | null
  assignee?: string | null
  assignee_name?: string | null
}

/** Which ledger lines a billable-lines listing holds. */
export type BillableLinesKind = "receivable" | "payable"

export interface InvoiceLineInput {
  finance_line: string
  amount: string
}

export const INVOICE_HEADER_SCALES = [75, 90, 100, 110, 125, 150] as const
export type InvoiceHeaderScale = (typeof INVOICE_HEADER_SCALES)[number]

export interface GenerateInvoicePayload {
  project?: string | null
  customer?: string | null
  due_date?: string
  date_issued?: string
  tax_pct?: string | null
  retainage_pct?: string | null
  service_type?: string | null
  /** Rich text (sanitised HTML) printed at the foot of the invoice: up to
   * 511 visible characters, links and up to 3 images. */
  notes?: string
  /** Sending it also makes it the company's default for the next invoices. */
  header_scale?: InvoiceHeaderScale
  lines?: InvoiceLineInput[]
  /** Issue or send in the same request, so a refusal leaves no draft. */
  then?: "issue" | "send"
  recipient?: string
}

export interface SendInvoicePayload {
  id: string
  recipient?: string
}

export interface InvoiceDelivery {
  id: string
  recipient: string
  channel: number
  channel_display?: string
  status: number
  status_display?: string
  sent_at: string | null
  sent_by?: string | null
  sent_by_name?: string | null
  error?: string
  created?: string
}

export interface SendInvoiceResponse {
  invoice: InvoiceListItem
  delivery: InvoiceDelivery
}

export interface BillableLinesDefaults {
  default_tax_pct?: string | null
  default_retainage_pct?: string | null
}

export interface BillableLinesResponse extends BillableLinesDefaults {
  lines: BillableLine[]
}

export interface BillableLinesResult {
  lines: BillableLine[]
  default_tax_pct: string | null
  default_retainage_pct: string | null
}

export interface InvoicesListFilters {
  page: number
  page_size: number
  q: string
  customer: string | null
  origin: number | null
  status: number | null
  date_issued_from: string | null
  date_issued_to: string | null
  due_date_from: string | null
  due_date_to: string | null
}

export type InvoicesListResponse = BasePagination<InvoiceListItem>

/** Mirrors InvoiceViewSet.summary — gross money totals over the whole
 * filtered collection, not just the loaded page. Amounts are strings. */
export interface InvoicesSummary {
  total: string
  pending: string
  overdue: string
  paid: string
  receivable: string
  payable: string
}

export const DEFAULT_INVOICES_SUMMARY: InvoicesSummary = {
  total: "0",
  pending: "0",
  overdue: "0",
  paid: "0",
  receivable: "0",
  payable: "0"
}

export interface InvoicesState {
  items: InvoiceListItem[]
  count: number
  /** The scrolled list's next page, or null once it has all of them. */
  next: string | null
  /** The page a load-more failed on, offered back by the list footer. */
  retryNext: string | null
  fetching: boolean
  requestId: string | null
  exporting: boolean
  filters: InvoicesListFilters
  ordering: string | null
  billableLines: BillableLine[]
  billableDefaults: BillableLinesDefaults
  billableLinesFetching: boolean
  /** The kind the held billable lines are; null before any listing. */
  billableLinesKind: BillableLinesKind | null
  /** The newest billable-lines request; only its answer is kept. */
  billableLinesRequestId: string | null
  saving: boolean
  summary: InvoicesSummary
  summaryFetching: boolean
  /** A failed summary must not render as four genuine $0 tiles. */
  summaryFailed: boolean
}

export const DEFAULT_INVOICES_FILTERS: InvoicesListFilters = {
  page: 1,
  page_size: SCROLL_PAGE_SIZE,
  q: "",
  customer: null,
  origin: null,
  status: null,
  date_issued_from: null,
  date_issued_to: null,
  due_date_from: null,
  due_date_to: null
}

/** A payment linked to an invoice, from `GET invoices/{id}/payments/`:
 * whichever finance line it is booked on, billed by the invoice or not. */
export interface InvoiceLinkedPayment {
  id: string
  value: string
  status: number
  status_display?: string
  payment_method_display?: string
  payment_date?: string | null
  invoice: string | null
  finance_line: string
  finance_line_name?: string
  /** Whether the invoice bills the line the payment is booked on. */
  billed_line: boolean
}
